import { useMemo, useState } from "react"
import { ChevronRight, LayoutDashboard, MessageSquare, Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { OutlineItem } from "@/lib/prompt-markdown"
import type { PromptSummary } from "@/lib/prompt-audit-api"

export const OVERVIEW_ID = "overview"

export const GROUP_ORDER = [
  "Primary agent",
  "Generation",
  "Edit",
  "Booking",
  "Grocery",
  "Validator",
  "Specialists",
  "Peptide reviewer",
  "Judges and small calls",
  "Constitution and Magna Carta",
  "Voice",
  "Guidance",
  "Dynamic context",
  "Booking playbooks",
  "Unavailable",
]

const COLLAPSED_BY_DEFAULT = new Set(["Booking playbooks"])

type NavProps = {
  entries: PromptSummary[]
  selected: string
  outlineItems: OutlineItem[]
  searchHits: Record<string, number> | null
  query: string
  onQuery: (query: string) => void
  onSelect: (id: string) => void
  onJump: (offset: number) => void
}

function Flags({ entry, hits }: { entry: PromptSummary; hits?: number }) {
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1.5">
      {hits !== undefined && (
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {hits}
          <span className="sr-only"> matches</span>
        </span>
      )}
      {entry.live_state === "absent" ? (
        <span className="rounded bg-amber-500/15 px-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300">MISSING</span>
      ) : entry.drift && <span className="rounded bg-amber-500/15 px-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300">DRIFT</span>}
      {entry.live_state === "not_loaded" && <span className="rounded bg-muted px-1 text-[10px] font-semibold text-muted-foreground">NOT LOADED</span>}
      {entry.error && <span className="rounded bg-destructive/15 px-1 text-[10px] font-semibold text-destructive">ERROR</span>}
      {entry.open_comments > 0 && (
        <span className="inline-flex items-center gap-0.5 text-[11px] tabular-nums text-muted-foreground">
          <MessageSquare className="size-3" aria-hidden="true" />
          <span className="sr-only">open comments:</span>
          {entry.open_comments}
        </span>
      )}
    </span>
  )
}

export function PromptNav({ entries, selected, outlineItems, searchHits, query, onQuery, onSelect, onJump }: NavProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(COLLAPSED_BY_DEFAULT))
  const groups = useMemo(() => {
    const visible = searchHits ? entries.filter((entry) => searchHits[entry.id] !== undefined) : entries
    const byGroup = new Map<string, PromptSummary[]>()
    for (const entry of visible) byGroup.set(entry.group, [...(byGroup.get(entry.group) ?? []), entry])
    return [...byGroup.entries()].sort(([a], [b]) => GROUP_ORDER.indexOf(a) - GROUP_ORDER.indexOf(b))
  }, [entries, searchHits])

  return (
    <nav aria-label="Prompts" className="flex h-full flex-col">
      <div className="border-b p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder="Search all prompt text"
            aria-label="Search all prompt text"
            className="pl-8"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <button
          type="button"
          onClick={() => onSelect(OVERVIEW_ID)}
          aria-current={selected === OVERVIEW_ID ? "page" : undefined}
          className={cn(
            "mb-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm",
            selected === OVERVIEW_ID ? "bg-accent font-medium text-accent-foreground" : "hover:bg-muted",
          )}
        >
          <LayoutDashboard className="size-4 text-muted-foreground" aria-hidden="true" />
          Overview
        </button>
        {searchHits && groups.length === 0 && (
          <p className="px-2 py-4 text-sm text-muted-foreground">No prompt contains that text.</p>
        )}
        {groups.map(([group, items]) => {
          const open = Boolean(searchHits) || !collapsed.has(group)
          return (
            <section key={group} className="mt-3">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setCollapsed((previous) => {
                  const next = new Set(previous)
                  if (next.has(group)) next.delete(group)
                  else next.add(group)
                  return next
                })}
                className="flex w-full items-center gap-1 rounded px-2 py-1 text-xs font-medium tracking-wide text-muted-foreground uppercase hover:text-foreground"
              >
                <ChevronRight className={cn("size-3 transition-transform", open && "rotate-90")} aria-hidden="true" />
                {group}
                <span className="ml-auto tabular-nums">{items.length}</span>
              </button>
              {open && (
                <ul className="mt-1 space-y-0.5">
                  {items.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(entry.id)}
                        aria-current={selected === entry.id ? "page" : undefined}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                          selected === entry.id ? "bg-accent font-medium text-accent-foreground" : "text-foreground/80 hover:bg-muted",
                        )}
                      >
                        <span className="truncate">{entry.title}</span>
                        <Flags entry={entry} hits={searchHits?.[entry.id]} />
                      </button>
                      {selected === entry.id && outlineItems.length > 0 && (
                        <ul aria-label="Sections" className="my-1 ml-3 space-y-0.5 border-l pl-2">
                          {outlineItems.map((item) => (
                            <li key={item.start}>
                              <button
                                type="button"
                                onClick={() => onJump(item.start)}
                                className="w-full truncate rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                                style={{ paddingLeft: `${0.5 + Math.max(item.level - 1, 0) * 0.6}rem` }}
                              >
                                {item.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )
        })}
      </div>
    </nav>
  )
}
